import React, {
  useRef,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import Leaflet from 'leaflet';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import getValidationErrors from '../../../utils/getValidationErrors';

import { IconChevronRight, IconTrash } from '@tabler/icons';

import BackHeader from '../../../components/BackHeader';
import Input from '../../../components/Form/Input';
import Button from '../../../components/Button';
import SwitchInput from '../../../components/Form/SwitchInput';
import Modal from '../../../components/MapModal';
import Offline from '../../../components/OfflineMessage';

import {
  Label,
  Skeleton,
  Row,
  DeleteButton,
} from '../../../components/Generic';
import {
  Container,
  Content,
  MapWrapper,
  InputLabel,
  FakeMap,
  LittleImage,
} from './styles';
import { Card, BlueLink } from '../styles';

import mapPinImage from '../../../assets/icons/map-pin.svg';

const mapPinIcon = Leaflet.icon({
  iconUrl: mapPinImage,
  iconSize: [50, 50],
  iconAnchor: [25, 50],
  popupAnchor: [0, 0],
});

const CustomMapComponent = ({ position }: any) => {
  const map = useMap();

  useEffect(() => {
    map.setView(position, map.getZoom());
  }, [position]);

  return null;
};

interface IPropertyData {
  nome: string;
  descricao: string;
  latitude?: number;
  longitude?: number;
  area_total?: number;
  privada?: true | false;
  area_produtiva?: number;
  categoria_animais?: string;
}

const NewProperty: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [property, setProperty] = useState<any>();
  const [position, setPosition] = useState<any>([0, 0]);

  const formRef = useRef<FormHandles>(null);
  const markerRef = useRef(null);

  const history = useHistory();
  const { id }: any = useParams();
  const { addToast } = useToast();
  const isOnline = useOnlineStatus();

  useEffect(() => {
    getProperty();
  }, []);

  const getProperty = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/propriedades/`, {
        headers: {
          propriedade_id: id,
        },
      });
      setProperty(response.data);

      if (response.data.latitude !== null)
        setPosition([response.data.latitude, response.data.longitude]);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPosition = () => {
    setIsModalOpen(false);

    formRef.current?.setFieldValue('latitude', position.lat);
    formRef.current?.setFieldValue('longitude', position.lng);
  };

  const handleAddPosition = () => {
    navigator.geolocation.getCurrentPosition(p => {
      setPosition([p.coords.latitude, p.coords.longitude]);
    });

    setIsModalOpen(true);
  };

  const handleSubmit = useCallback(
    async (data: IPropertyData) => {
      try {
        setActionLoading(true);

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          nome: Yup.string().required('Nome obrigatório.'),
          descricao: Yup.string().required('Localização obrigatória.'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api.put(
          `/propriedades/`,
          {
            nome: data.nome,
            descricao: data.descricao,
            latitude: data.latitude,
            longitude: data.longitude,
            privada: data.privada,
            area_total: data.area_total,
            area_produtiva: data.area_produtiva,
            categoria_animais: data.categoria_animais,
          },
          {
            headers: {
              propriedade_id: id,
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'A propriedade foi atualizada.',
        });

        history.goBack();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message,
        });
      } finally {
        setActionLoading(false);
      }
    },
    [addToast],
  );

  const mapEventHandlers = useMemo(
    () => ({
      dragend() {
        const marker: any = markerRef.current;

        if (marker != null) {
          setPosition(marker.getLatLng());
        }
      },
    }),
    [],
  );

  const handleDelete = async () => {
    if (
      window.confirm(
        'Tem certeza? A exclusão de uma propriedade é irreversível.',
      )
    ) {
      try {
        await api.delete(`/propriedades/`, {
          headers: {
            propriedade_id: id,
          },
        });

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Propriedade deletada.',
        });

        history.goBack();
        history.goBack();
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado.',
        });
      }
    }
  };

  return (
    <Container>
      <BackHeader title="Editar propriedade" />

      {!isOnline ? (
        <Offline />
      ) : loading ? (
        <Content>
          <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
          <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
          <Skeleton style={{ height: 40, width: '100%', marginBottom: 20 }} />
          <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
          <Skeleton style={{ height: 150, width: '100%', marginBottom: 30 }} />
        </Content>
      ) : (
        <Content>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <div style={{ display: 'none' }}>
              <Input
                label="Longitude"
                name="longitude"
                type="text"
                defaultValue={property?.longitude}
              />

              <Input
                label="Latitude"
                name="latitude"
                type="text"
                defaultValue={property?.latitude}
              />
            </div>

            <Input
              label="Nome"
              name="nome"
              type="text"
              defaultValue={property?.nome}
            />

            <Input
              label="Localização"
              name="descricao"
              type="text"
              placeholder="Ex: Aquidauana - MS"
              defaultValue={property?.descricao}
            />

            <SwitchInput
              label="Visibilidade privada"
              name="privada"
              defaultChecked={property?.privada}
            />

            <Label>Opcionais</Label>

            <InputLabel>Posição no mapa</InputLabel>

            {position[0] !== 0 && !loading ? (
              <MapWrapper onClick={() => setIsModalOpen(true)}>
                <div />

                <MapContainer
                  center={position}
                  zoom={13}
                  scrollWheelZoom={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; Contribuidores do <a href="http://osm.org/copyright">OpenStreetMap</a>'
                  />

                  <Marker position={position} icon={mapPinIcon} />

                  <CustomMapComponent position={position} />
                </MapContainer>
              </MapWrapper>
            ) : (
              <FakeMap type="button" onClick={handleAddPosition}>
                <img src={mapPinImage} alt="Local" />
                <span>Selecionar um local</span>
              </FakeMap>
            )}

            <Input
              label="Área total (hectares)"
              name="area_total"
              type="number"
              defaultValue={property?.area_total}
            />

            <Input
              label="Área produtiva (hectares)"
              name="area_produtiva"
              type="number"
              defaultValue={property?.area_produtiva}
            />

            <Input
              label="Categoria de animais"
              name="categoria_animais"
              type="text"
              defaultValue={property?.categoria_animais}
            />

            <Card>
              <Row
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <label>Imagens</label>

                <BlueLink
                  onClick={() =>
                    history.push(`/propriedade/imagens/${property.id}`)
                  }
                >
                  Gerenciar
                  <IconChevronRight size={16} />
                </BlueLink>
              </Row>

              <Row
                style={{
                  maxHeight: 70,
                  padding: '0 16px',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                {property?.imagens?.map((image: any) => (
                  <LittleImage src={image.image_url} />
                ))}
              </Row>
            </Card>

            <Button
              type="submit"
              isLoading={actionLoading}
              style={{ marginTop: 60 }}
            >
              Salvar
            </Button>

            <DeleteButton type="button" onClick={handleDelete}>
              <IconTrash size={25} />
              Excluir
            </DeleteButton>
          </Form>
        </Content>
      )}

      <Modal
        label="Confirmar posição"
        isOpen={isModalOpen}
        definePosition={true}
        onClose={() => handleSetPosition()}
        onGetLocal={() => handleAddPosition()}
      >
        <MapContainer
          center={position}
          zoom={15}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; Contribuidores do <a href="http://osm.org/copyright">OpenStreetMap</a>'
          />

          <Marker
            draggable={true}
            eventHandlers={mapEventHandlers}
            position={position}
            ref={markerRef}
            icon={mapPinIcon}
          />

          <CustomMapComponent position={position} />
        </MapContainer>
      </Modal>
    </Container>
  );
};

export default NewProperty;
